import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/useStore';
import { useApi } from '../../hooks/useApi';
import { DealsFirstTagTreeRequest } from '../../api/analytics/dto/DealsFirstTagTreeRequest.dto';
import {
  TreeItem,
  StageMetrics,
} from '../../api/analytics/dto/DealsFirstTagTreeResponse.dto';
import DealsModal from '../../components/popUp/DealsModal';
import DatePickerLocal from '../../components/UI/DatePicker/datePickerLocal';
import styles from './DealsTree.module.css';

interface ParentData {
  tag?: string;
  bucket?: 'new' | 'old' | 'repeat';
  responsible?: string;
  source?: string;
  medium?: string;
}

interface ExpandedNode {
  level: 'tag' | 'bucket' | 'responsible' | 'source' | 'medium' | 'campaign';
  parent: ParentData;
  items: TreeItem[];
  loading: boolean;
  offset: number;
  hasMore: boolean;
}

const DealsTreePage: React.FC = observer(() => {
  const { dateStore } = useStores();
  const api = useApi();

  const [rootItems, setRootItems] = useState<TreeItem[]>([]);
  const [rootLoading, setRootLoading] = useState(false);
  const [rootOffset, setRootOffset] = useState(0);
  const [rootHasMore, setRootHasMore] = useState(true);

  const [expandedNodes, setExpandedNodes] = useState<Map<string, ExpandedNode>>(
    new Map(),
  );
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [loadingMoreNodes, setLoadingMoreNodes] = useState<Set<string>>(
    new Set(),
  );

  // Состояние для модального окна
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalFilters, setModalFilters] = useState<ParentData>({});
  const [modalTitle, setModalTitle] = useState('');

  // Состояние для сортировки
  const [sortField, setSortField] = useState<
    | 'total_deals'
    | 'revenue'
    | 'avg_order_value'
    | 'avg_closing_time_hours'
    | 'completed'
    | 'failed'
    | 'in_progress'
    | 'leads_pool'
    | 'no_answer'
    | 'not_started'
  >('total_deals');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Состояние для управления датой закрытия
  const [useCompletionDate, setUseCompletionDate] = useState(false);
  const [completionDateFrom, setCompletionDateFrom] = useState<string>('');
  const [completionDateTo, setCompletionDateTo] = useState<string>('');

  // Состояние для контроля запросов
  const [, setActiveRequestController] = useState<AbortController | null>(null);
  const [, setRequestDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [, setActiveNodeRequests] = useState<Map<string, AbortController>>(
    new Map(),
  );

  const handleCompletionDateChange = (dates: [string, string]) => {
    setCompletionDateFrom(dates[0]);
    setCompletionDateTo(dates[1]);
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const ITEMS_PER_PAGE = 10;

  const generateNodeKey = (level: string, parent: ParentData): string => {
    const parts = [level];
    if (parent.tag) {
      parts.push(`tag:${parent.tag}`);
    }
    if (parent.bucket) {
      parts.push(`bucket:${parent.bucket}`);
    }
    if (parent.responsible) {
      parts.push(`responsible:${parent.responsible}`);
    }
    if (parent.source) {
      parts.push(`source:${parent.source}`);
    }
    if (parent.medium) {
      parts.push(`medium:${parent.medium}`);
    }
    return parts.join('|');
  };

  const loadRootData = useCallback(
    async (offset: number = 0, append: boolean = false) => {
      if (!dateStore.startDate || !dateStore.endDate) {
        return;
      }

      // Отменяем предыдущий запрос если он есть
      setActiveRequestController((current) => {
        if (current) {
          current.abort();
        }
        return null;
      });

      // Создаем новый AbortController для этого запроса
      const controller = new AbortController();
      setActiveRequestController(controller);

      setRootLoading(true);

      try {
        const request: DealsFirstTagTreeRequest = {
          level: 'tag',
          filters: {
            date_from: dateStore.startDate,
            date_to: dateStore.endDate,
            ...(useCompletionDate &&
              completionDateFrom &&
              completionDateTo && {
                date_from_completed: completionDateFrom,
                date_to_completed: completionDateTo,
              }),
            only_not_archived: true,
            only_not_deleted: true,
          },
          pagination: {
            limit: ITEMS_PER_PAGE,
            offset,
          },
          sort: {
            field: sortField as
              | 'total_deals'
              | 'revenue'
              | 'avg_order_value'
              | 'avg_closing_time_hours'
              | 'completed'
              | 'failed'
              | 'in_progress'
              | 'leads_pool'
              | 'no_answer'
              | 'not_started',
            order: sortOrder,
          },
        };

        const response = await api.analytics.getDealsFirstTagTree(request);

        if (
          response.success &&
          response.body &&
          Array.isArray(response.body.items)
        ) {
          const newItems = response.body.items.filter(
            (item) => item && typeof item === 'object',
          );

          if (append) {
            setRootItems((prev) => [...prev, ...newItems]);
          } else {
            setRootItems(newItems);
          }

          setRootHasMore(newItems.length === ITEMS_PER_PAGE);
          setRootOffset(offset + newItems.length);
        }
      } catch (error) {
        // Проверяем, не была ли операция отменена
        if (error instanceof Error && error.name === 'AbortError') {
          return; // Не обрабатываем отмененные запросы как ошибки
        }
        // eslint-disable-next-line no-console
        console.error('Error loading root data:', error);
      } finally {
        setRootLoading(false);
        // Очищаем активный контроллер только если это наш запрос
        setActiveRequestController((current) =>
          current === controller ? null : current,
        );
      }
    },
    [
      api.analytics,
      dateStore.startDate,
      dateStore.endDate,
      sortField,
      sortOrder,
      useCompletionDate,
      completionDateFrom,
      completionDateTo,
    ],
  );

  // Функция для отмены активного запроса и очистки таймера
  const cancelActiveRequest = useCallback(() => {
    setActiveRequestController((current) => {
      if (current) {
        current.abort();
      }
      return null;
    });

    setRequestDebounceTimer((current) => {
      if (current) {
        clearTimeout(current);
      }
      return null;
    });

    // Отменяем все активные запросы для дочерних узлов
    setActiveNodeRequests((current) => {
      current.forEach((controller) => {
        controller.abort();
      });
      return new Map();
    });
  }, []);

  // Дебаунсированная функция для загрузки данных
  const debouncedLoadData = useCallback(() => {
    // Отменяем предыдущий запрос
    cancelActiveRequest();

    // Устанавливаем новый таймер
    const timer = setTimeout(() => {
      setRootItems([]);
      setRootOffset(0);
      setRootHasMore(true);
      setExpandedNodes(new Map());
      loadRootData(0, false);
    }, 300); // 300ms задержка

    setRequestDebounceTimer(timer);
  }, [cancelActiveRequest, loadRootData]);

  // Перезагружаем данные при изменении сортировки или даты закрытия
  useEffect(() => {
    debouncedLoadData();
  }, [
    sortField,
    sortOrder,
    useCompletionDate,
    completionDateFrom,
    completionDateTo,
    debouncedLoadData,
  ]);

  // Cleanup функция для отмены активных запросов при размонтировании
  useEffect(() => {
    return () => {
      // Используем функциональные обновления для доступа к актуальным значениям
      setActiveRequestController((current) => {
        if (current) {
          current.abort();
        }
        return null;
      });

      setRequestDebounceTimer((current) => {
        if (current) {
          clearTimeout(current);
        }
        return null;
      });

      setActiveNodeRequests((current) => {
        current.forEach((controller) => {
          controller.abort();
        });
        return new Map();
      });
    };
  }, []);

  const loadNodeData = useCallback(
    async (
      level: 'bucket' | 'responsible' | 'source' | 'medium' | 'campaign',
      parent: ParentData,
      offset: number = 0,
      append: boolean = false,
    ) => {
      if (!dateStore.startDate || !dateStore.endDate) {
        return;
      }

      const nodeKey = generateNodeKey(level, parent);

      // Отменяем предыдущий запрос для этого узла, если он есть
      setActiveNodeRequests((prev) => {
        const existingController = prev.get(nodeKey);
        if (existingController) {
          existingController.abort();
        }
        return prev;
      });

      // Создаем новый AbortController для этого запроса
      const controller = new AbortController();
      setActiveNodeRequests((prev) => {
        const newMap = new Map(prev);
        newMap.set(nodeKey, controller);
        return newMap;
      });

      setExpandedNodes((prev) => {
        const newMap = new Map(prev);
        const node = newMap.get(nodeKey);
        if (node) {
          node.loading = true;
        }
        return newMap;
      });

      try {
        const request: DealsFirstTagTreeRequest = {
          level,
          parent,
          filters: {
            date_from: dateStore.startDate,
            date_to: dateStore.endDate,
            ...(useCompletionDate &&
              completionDateFrom &&
              completionDateTo && {
                date_from_completed: completionDateFrom,
                date_to_completed: completionDateTo,
              }),
            only_not_archived: true,
            only_not_deleted: true,
          },
          pagination: {
            limit: ITEMS_PER_PAGE,
            offset,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        };

        const response = await api.analytics.getDealsFirstTagTree(request);

        if (
          response.success &&
          response.body &&
          Array.isArray(response.body.items)
        ) {
          const newItems = response.body.items.filter(
            (item) => item && typeof item === 'object',
          );

          setExpandedNodes((prev) => {
            const newMap = new Map(prev);
            const existingNode = newMap.get(nodeKey);

            if (existingNode && append) {
              existingNode.items = [...(existingNode.items || []), ...newItems];
              existingNode.offset = offset + newItems.length;
              existingNode.hasMore = newItems.length === ITEMS_PER_PAGE;
            } else {
              newMap.set(nodeKey, {
                level,
                parent,
                items: newItems,
                loading: false,
                offset: offset + newItems.length,
                hasMore: newItems.length === ITEMS_PER_PAGE,
              });
            }

            return newMap;
          });
        }
      } catch (error) {
        // Проверяем, не была ли операция отменена
        if (error instanceof Error && error.name === 'AbortError') {
          return; // Не обрабатываем отмененные запросы как ошибки
        }
        // eslint-disable-next-line no-console
        console.error('Error loading node data:', error);
      } finally {
        // Очищаем активный контроллер для этого узла
        setActiveNodeRequests((prev) => {
          const newMap = new Map(prev);
          if (newMap.get(nodeKey) === controller) {
            newMap.delete(nodeKey);
          }
          return newMap;
        });

        setExpandedNodes((prev) => {
          const newMap = new Map(prev);
          const node = newMap.get(nodeKey);
          if (node) {
            node.loading = false;
          }
          return newMap;
        });
      }
    },
    [
      api.analytics,
      dateStore.startDate,
      dateStore.endDate,
      sortField,
      sortOrder,
      useCompletionDate,
      completionDateFrom,
      completionDateTo,
    ],
  );

  const handleToggleExpand = (
    item: TreeItem,
    currentLevel: string,
    parent: ParentData = {},
  ) => {
    if (!item.has_children) {
      return;
    }

    const nextLevel = getNextLevel(currentLevel);
    if (!nextLevel) {
      return;
    }

    const newParent: ParentData = { ...parent };
    (newParent as Record<string, string>)[currentLevel] = item.key;

    const nodeKey = generateNodeKey(nextLevel, newParent);

    if (expandedNodes.has(nodeKey)) {
      // Collapse
      setExpandedNodes((prev) => {
        const newMap = new Map(prev);
        newMap.delete(nodeKey);
        return newMap;
      });
      setLoadingNodes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(nodeKey);
        return newSet;
      });
    } else {
      // Expand - добавляем в загружающиеся узлы
      setLoadingNodes((prev) => new Set(prev).add(nodeKey));

      loadNodeData(
        nextLevel as
          | 'bucket'
          | 'responsible'
          | 'source'
          | 'medium'
          | 'campaign',
        newParent,
      ).finally(() => {
        // Убираем из загружающихся узлов после завершения загрузки
        setLoadingNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeKey);
          return newSet;
        });
      });
    }
  };

  const getNextLevel = (currentLevel: string): string | null => {
    const levels = [
      'tag',
      'bucket',
      'responsible',
      'source',
      'medium',
      'campaign',
    ];
    const currentIndex = levels.indexOf(currentLevel);
    return currentIndex < levels.length - 1 ? levels[currentIndex + 1] : null;
  };

  const loadMoreRoot = () => {
    if (!rootLoading && rootHasMore) {
      loadRootData(rootOffset, true);
    }
  };

  const loadMoreNode = (nodeKey: string) => {
    const node = expandedNodes.get(nodeKey);
    if (node && !node.loading && node.hasMore && node.level !== 'tag') {
      setLoadingMoreNodes((prev) => new Set(prev).add(nodeKey));

      loadNodeData(
        node.level as
          | 'bucket'
          | 'responsible'
          | 'source'
          | 'medium'
          | 'campaign',
        node.parent,
        node.offset,
        true,
      ).finally(() => {
        setLoadingMoreNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nodeKey);
          return newSet;
        });
      });
    }
  };

  useEffect(() => {
    if (dateStore.startDate && dateStore.endDate) {
      debouncedLoadData();
    }
  }, [dateStore.startDate, dateStore.endDate, debouncedLoadData]);

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined || isNaN(num)) {
      return '-';
    }
    return num.toLocaleString('ru-RU');
  };

  const formatTime = (hours: number | null | undefined): string => {
    if (hours === null || hours === undefined || isNaN(hours)) {
      return '-';
    }
    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);
    return `${days}д ${remainingHours}ч`;
  };

  const formatMoney = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '-';
    }
    return `${Math.round(amount).toLocaleString('ru-RU')} $`;
  };

  const formatPercentage = (
    completed: number | null | undefined,
    total: number | null | undefined,
  ): string => {
    if (!completed || !total || total === 0) {
      return '0%';
    }
    const percentage = Math.round((completed / total) * 100);
    return `${percentage}%`;
  };

  const formatRevenuePerDeal = (
    revenue: number | null | undefined,
    totalDeals: number | null | undefined,
  ): string => {
    if (!revenue || !totalDeals || totalDeals === 0) {
      return '-';
    }
    const revenuePerDeal = revenue / totalDeals;
    return `${revenuePerDeal.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} $`;
  };

  const getStageValue = (
    stageMetrics: StageMetrics | undefined | null,
    stage: keyof StageMetrics,
  ): number => {
    if (!stageMetrics) {
      return 0;
    }
    return stageMetrics[stage] || 0;
  };

  const openDealsModal = (filters: ParentData, title: string) => {
    setModalFilters(filters);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const closeDealsModal = () => {
    setIsModalOpen(false);
    setModalFilters({});
    setModalTitle('');
  };

  const renderTreeItem = (
    item: TreeItem,
    level: string,
    parent: ParentData = {},
    depth: number = 0,
  ) => {
    const nodeKey = generateNodeKey(getNextLevel(level) || '', {
      ...parent,
      [level]: item.key,
    } as ParentData);
    const isExpanded = expandedNodes.has(nodeKey);
    const expandedNode = expandedNodes.get(nodeKey);
    const isLoading = loadingNodes.has(nodeKey);

    return (
      <div
        key={`${level}-${item.key || 'unknown'}`}
        className={styles.treeItem}
      >
        <div
          className={styles.itemRow}
          style={{ paddingLeft: `${depth * 20}px` }}
        >
          <div className={styles.itemName}>
            {item.has_children && (
              <>
                {isLoading ? (
                  <div className={styles.loadingSpinner} />
                ) : (
                  <button
                    className={styles.expandButton}
                    onClick={() => handleToggleExpand(item, level, parent)}
                    disabled={isLoading}
                  >
                    {isExpanded ? '−' : '+'}
                  </button>
                )}
              </>
            )}
            <span
              className={styles.name}
              onClick={() => {
                const filters: ParentData = {
                  ...parent,
                  [level]: item.key || '',
                };
                openDealsModal(filters, `Сделки: ${item.key || 'Неизвестно'}`);
              }}
            >
              {item.key || 'Неизвестно'}
            </span>
            {level === 'tag' && item.buckets && (
              <div className={styles.buckets}>
                <span
                  className={styles.bucket}
                  onClick={() => {
                    const filters: ParentData = {
                      ...parent,
                      [level]: item.key || '',
                      bucket: 'new',
                    };
                    openDealsModal(
                      filters,
                      `Новые клиенты: ${item.key || 'Неизвестно'}`,
                    );
                  }}
                >
                  Новые: {item.buckets?.new || 0}
                </span>
                <span
                  className={styles.bucket}
                  onClick={() => {
                    const filters: ParentData = {
                      ...parent,
                      [level]: item.key || '',
                      bucket: 'old',
                    };
                    openDealsModal(
                      filters,
                      `Старые клиенты: ${item.key || 'Неизвестно'}`,
                    );
                  }}
                >
                  Старые: {item.buckets?.old || 0}
                </span>
                <span
                  className={styles.bucket}
                  onClick={() => {
                    const filters: ParentData = {
                      ...parent,
                      [level]: item.key || '',
                      bucket: 'repeat',
                    };
                    openDealsModal(
                      filters,
                      `Повторные клиенты: ${item.key || 'Неизвестно'}`,
                    );
                  }}
                >
                  Повторные: {item.buckets?.repeat || 0}
                </span>
              </div>
            )}
          </div>

          <div className={styles.metrics}>
            <div className={styles.metric}>
              {formatNumber(item.metrics?.total_deals || 0)}
            </div>
            <div className={styles.metric}>
              {formatNumber(
                getStageValue(item.metrics?.stage_metrics, 'completed'),
              )}
            </div>
            <div className={styles.metric}>
              {formatPercentage(
                getStageValue(item.metrics?.stage_metrics, 'completed'),
                item.metrics?.total_deals,
              )}
            </div>
            <div className={styles.metric}>
              {formatMoney(item.metrics?.revenue || null)}
            </div>
            <div className={styles.metric}>
              {formatMoney(item.metrics?.avg_order_value || null)}
            </div>
            <div className={styles.metric}>
              {formatRevenuePerDeal(
                item.metrics?.revenue,
                item.metrics?.total_deals,
              )}
            </div>
            <div className={styles.metric}>
              {formatTime(item.metrics?.avg_closing_time_hours || null)}
            </div>
            <div className={styles.metric}>
              {formatNumber(
                getStageValue(item.metrics?.stage_metrics, 'in_progress'),
              )}
            </div>
            <div className={styles.metric}>
              {formatNumber(
                getStageValue(item.metrics?.stage_metrics, 'failed'),
              )}
            </div>
            <div className={styles.metric}>
              {formatPercentage(
                getStageValue(item.metrics?.stage_metrics, 'failed'),
                item.metrics?.total_deals,
              )}
            </div>
          </div>
        </div>

        {isExpanded && expandedNode && (
          <div className={styles.childrenContainer}>
            {expandedNode.items && expandedNode.items.length > 0
              ? expandedNode.items.map((childItem) =>
                  renderTreeItem(
                    childItem,
                    expandedNode.level,
                    expandedNode.parent,
                    depth + 1,
                  ),
                )
              : !expandedNode.loading && (
                  <div
                    className={styles.emptyState}
                    style={{ paddingLeft: `${(depth + 1) * 20}px` }}
                  >
                    Нет данных
                  </div>
                )}

            {expandedNode.loading && (
              <div
                className={styles.loader}
                style={{ paddingLeft: `${(depth + 1) * 20}px` }}
              >
                Загрузка...
              </div>
            )}

            {expandedNode.hasMore && !expandedNode.loading && (
              <button
                className={styles.loadMoreButton}
                style={{ marginLeft: `${(depth + 1) * 20}px` }}
                onClick={() => loadMoreNode(nodeKey)}
                disabled={loadingMoreNodes.has(nodeKey)}
              >
                {loadingMoreNodes.has(nodeKey) ? (
                  <>
                    <div className={styles.loadingSpinner} />
                    Загрузка...
                  </>
                ) : (
                  'Загрузить ещё'
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Дерево сделок по тегам</h1>
        <div className={styles.dateControls}>
          <label className={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={useCompletionDate}
              onChange={(e) => setUseCompletionDate(e.target.checked)}
            />
            <span className={styles.checkboxLabel}>
              Учитывать дату закрытия
            </span>
          </label>
          {useCompletionDate && (
            <div className={styles.datePickerContainer}>
              <span className={styles.datePickerLabel}>Дата закрытия:</span>
              <DatePickerLocal onChangeDate={handleCompletionDateChange} />
            </div>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.headerName}>Название</div>
          <div className={styles.headerMetrics}>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('total_deals')}
            >
              Сделок
              {sortField === 'total_deals' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('completed')}
            >
              Реализованно
              {sortField === 'completed' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div>% реализации</div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('revenue')}
            >
              Доход
              {sortField === 'revenue' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('avg_order_value')}
            >
              Ср. чек
              {sortField === 'avg_order_value' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div className={styles.sortableHeader}>SQL</div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('avg_closing_time_hours')}
            >
              Время закрытия
              {sortField === 'avg_closing_time_hours' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('in_progress')}
            >
              В работе
              {sortField === 'in_progress' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div
              className={styles.sortableHeader}
              onClick={() => handleSort('failed')}
            >
              Не реализованно
              {sortField === 'failed' && (
                <span className={styles.sortArrow}>
                  {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                </span>
              )}
            </div>
            <div>% не реализованно</div>
          </div>
        </div>

        <div className={styles.tableBody}>
          {rootItems && rootItems.length > 0
            ? rootItems.map((item) => renderTreeItem(item, 'tag'))
            : !rootLoading && (
                <div className={styles.emptyState}>
                  Нет данных для отображения
                </div>
              )}

          {rootLoading && <div className={styles.loader}>Загрузка...</div>}

          {rootHasMore && !rootLoading && (
            <button
              className={styles.loadMoreButton}
              onClick={loadMoreRoot}
              disabled={rootLoading}
            >
              {rootLoading ? (
                <>
                  <div className={styles.loadingSpinner} />
                  Загрузка...
                </>
              ) : (
                'Загрузить ещё'
              )}
            </button>
          )}
        </div>
      </div>

      <DealsModal
        isOpen={isModalOpen}
        onClose={closeDealsModal}
        filters={modalFilters}
        title={modalTitle}
        completionDateFilters={{
          useCompletionDate,
          completionDateFrom,
          completionDateTo,
        }}
      />
    </div>
  );
});

export default DealsTreePage;
